// shared-lib/cabinet-runs/vanityToRun.js
//
// Derive an editable cabinet RUN from a vanity fixture group — the bathroom
// twin of `cabinetLayoutToRuns` (generated kitchen) and `deriveScanRuns`
// (scanned kitchen), and phases 3a and 3b of the bathroom-cabinet-runs plan.
//
// ONE function serves both phases because a vanity is the same shape however
// it was built: a cabinet on a wall with one or two basins in its counter.
// Only `source` differs — `'auto'` for a vanity the generator or a chat tool
// just built, `'scan'` for one the user converts — and `source` is provenance:
// it decides whether a re-solve stamps `userEdited` and whether the run
// regenerates when its wall moves, never how the run is derived.
//
// It is DELIBERATELY NOT `deriveScanRuns` with a bathroom argument. That
// module recovers a COUNTER: it clusters bodies across captured wall
// segments, joins runs at corners, and grows a run end into a closed inside
// corner (`runEndClosure`). Every one of those is right for a kitchen and
// wrong for a vanity — a vanity is a discrete piece of furniture, and growing
// one to the corner of the room would invent cabinetry nobody has.
//
// DERIVING IS NON-DESTRUCTIVE, exactly as it is for a scanned kitchen: this
// emits a run spec and stamps `runId` on the cabinet and its countertop. It
// moves nothing. The vanity renders as it did until the user's first
// deliberate bay edit, when the engine re-solves the span into flush module
// bays like any other run.
//
// THE BASIN AND FAUCET ARE NOT TAGGED. They are the vanity's — measured on a
// scan, chosen from the catalog on a build — so the run RESERVES their
// footprint as an appliance bay and never re-emits them, which is the rule a
// scanned kitchen sink already follows (`incumbents.reseatSinkHosts`, and
// `applyApplianceMoves` is what carries a basin along when its bay moves).
// The plan's first sketch had them `runId`-tagged from birth; that would make
// the fill rebuild the basin under its own `sink_<bayId>` id and the merge
// sweep the original, throwing away the product the layout picked — unless
// every id were re-keyed at derive time, which is a lot of churn to buy
// nothing the incumbent route does not already give.
//
// Pure. No I/O.

const { VANITY_PROFILE_ID } = require('./profiles');
const { familyFieldsForNewRun } = require('./newRunFamily');

// Metres per inch — only the module threshold below needs it.
const IN_M = 0.0254;
const { applianceBayFromLegacy } = require('./bays');
const { vanitySinkPlan } = require('./vanitySinkBay');
const { AS_BUILT, STANDARD } = require('./sizing');
const { stampApplianceBays } = require('./ownership');
const { stampRunFrames } = require('./wallFrame');
const { isAxisAlignedToWall } = require('./sameWall');
const { carcassCanCarryBasin } = require('../vanity-assembly/counterOverhang');
const { dedupeRoomWalls } = require('../kitchen-assembly/wallDedupe');
const {
  getWallAxes,
  worldToWallParam,
  objectFootprint,
} = require('../kitchen-assembly/wallMath');
const {
  getVanityCabinet,
  getVanityCountertop,
  getFixtureGroupSinks,
  isStandaloneSink,
  isVanityAssemblyGroupId,
} = require('../vanity-assembly/fixtureGroup');
const { isLowerCabinetLike } = require('../group-customization/anchorTypes');
const { APPEARANCE_FIELDS } = require('./bayOverrides');
const { COUNTERTOP_APPEARANCE_FIELDS } = require('./countertopAppearance');
const { sameStoredValue } = require('../rooms/sameStoredValue');
// Re-exported below so existing callers keep their import path.
const { vanityRunId, isVanityRunId } = require('./runIds');

/**
 * The look an object WEARS over `fields`: each field it sets — a null
 * included, the record's "explicit not this" — and none it leaves undefined,
 * which Firestore would refuse anywhere in a document. Null when it sets none
 * of them to a value.
 */
function lookWorn(object, fields) {
  const look = {};
  for (const f of fields) if (object?.[f] !== undefined) look[f] = object[f];
  return fields.some((f) => look[f] != null) ? look : null;
}

const sameLookOver = (a, b, fields) =>
  fields.every((f) => sameStoredValue(a?.[f] ?? null, b?.[f] ?? null));

/**
 * The looks a run with NO cabinet line takes from the vanity it is derived
 * from: the cabinet's finish and hardware as its `appearance`, the counter's
 * as its `countertopAppearance` — each only where it is not the room's look
 * already, which the run would wear anyway.
 *
 * Deriving is non-destructive, and the look is part of that: a run with no
 * look of its own dresses every box it rebuilds in the room's
 * (`bayOverrides.effectiveAppearance`), which is usually none, so the first
 * fill of a styled vanity used to come out in the default finish with no
 * handles — the textured, chrome-handled box a layout built, rebuilt plain
 * (2026-11-02). A run in a line takes the line's look instead
 * (`familyFieldsForNewRun`), and that is left as it is.
 */
function wornLookFields(room, cabinet, countertop) {
  const out = {};
  const cabinetLook = lookWorn(cabinet, APPEARANCE_FIELDS);
  if (
    cabinetLook &&
    !sameLookOver(cabinetLook, room?.cabinetAppearance, APPEARANCE_FIELDS)
  ) {
    out.appearance = cabinetLook;
  }
  const counterLook = lookWorn(countertop, COUNTERTOP_APPEARANCE_FIELDS);
  if (
    counterLook &&
    !sameLookOver(
      counterLook,
      room?.countertopAppearance,
      COUNTERTOP_APPEARANCE_FIELDS
    )
  ) {
    out.countertopAppearance = counterLook;
  }
  return out;
}

/**
 * The span a body occupies on its wall, in that wall's own param frame.
 *
 * Measured from the FOOTPRINT rather than from `center ± width/2`: the body
 * may be a few degrees off the wall (a scan always is), and the footprint's
 * projection is the extent the fill has to cover either way.
 */
function spanOnWall(body, wall) {
  const axes = getWallAxes(wall);
  const corners = objectFootprint(body);
  if (!axes || !corners) return null;
  const params = corners.map(
    (c) =>
      (c.x - axes.start.x) * axes.wallDir.x +
      (c.z - axes.start.z) * axes.wallDir.z
  );
  return { startParam: Math.min(...params), endParam: Math.max(...params) };
}

/**
 * A vanity as the sink-bay rule reads it: its WALL, its span along that wall
 * (`lo`..`hi`, the wall's own param frame) and each basin's place on it —
 * `{ id, bowl, centerParam }`. `{ warning }` when it cannot be measured: no
 * wall of this room, at an angle to its wall, a degenerate span.
 *
 * One measurement for the derive and for the builder that seats a line
 * vanity's bowls before it (`lineVanity.seatVanityBasins`), so the two ask the
 * rule the same question.
 */
function vanityOnWall(room, cabinet, groupObjects, groupId) {
  const wallId = (cabinet.parentWallIds || [])[0];
  const wall = wallId
    ? dedupeRoomWalls(room?.walls || []).find((w) => w?.id === wallId)
    : null;
  if (!wall) {
    return {
      warning: `vanity ${cabinet.id} is not parented to a wall of this room — no run`,
    };
  }
  if (!isAxisAlignedToWall(cabinet, wall)) {
    return {
      warning:
        `vanity ${cabinet.id} sits at an angle to wall ${wallId} — ` +
        'its span cannot be measured in that frame, so no run',
    };
  }
  const extent = spanOnWall(cabinet, wall);
  if (!extent || extent.endParam <= extent.startParam) {
    return { warning: `vanity ${cabinet.id} has a degenerate span — no run` };
  }
  const basins = getFixtureGroupSinks(groupObjects, groupId)
    .filter((b) => b?.center && b?.dimensions)
    .map((basin) => ({
      id: basin.id,
      bowl: basin.dimensions.x,
      centerParam: worldToWallParam(wall, basin.center),
    }))
    .filter((b) => Number.isFinite(b.centerParam));
  return {
    wall,
    wallId,
    extent,
    lo: Math.min(extent.startParam, extent.endParam),
    hi: Math.max(extent.startParam, extent.endParam),
    basins,
  };
}

/**
 * Derive a run for ONE vanity group.
 *
 * @param {Object}   room      room doc with `walls` and `objects`.
 * @param {Object}   options
 * @param {string}   [options.fixtureGroupId] the group to convert. Required,
 *   unless `options.objects` is given — a caller that just BUILT a vanity can
 *   hand its objects straight in and the group is read off them. Never
 *   inferred from the room: a bathroom holds several groups, and guessing
 *   which one was meant is not this function's call (`deriveVanityRuns`
 *   enumerates them).
 * @param {Object[]} [options.objects] the group's objects, when the caller
 *   has them and the room doc does not yet (the build path). Defaults to the
 *   room's own objects filtered by group.
 * @param {'auto'|'scan'} [options.source='auto']
 * @returns {{run: Object|null, objects: Object[], warnings: string[]}}
 *   `run` is a stored-shape run, or null with a warning saying why. `objects`
 *   is the room's objects with `runId` / `bayId` stamped — the input array by
 *   IDENTITY when no run was derived, so a caller can skip a write.
 */
function vanityToRun(room, options = {}) {
  const roomObjects = room?.objects || [];
  const warnings = [];
  const nothing = { run: null, objects: roomObjects, warnings };

  const supplied = options.objects || null;
  const groupId =
    options.fixtureGroupId ||
    (supplied || []).find((o) => o?.fixtureGroupId)?.fixtureGroupId ||
    null;
  if (!groupId) {
    warnings.push('no fixture group named — nothing to convert');
    return nothing;
  }
  const groupObjects =
    supplied || roomObjects.filter((o) => o?.fixtureGroupId === groupId);
  if (!groupObjects.length) {
    warnings.push(`group ${groupId} has no objects`);
    return nothing;
  }

  const cabinet = getVanityCabinet(groupObjects);
  if (!cabinet || !cabinet.center || !cabinet.dimensions) {
    warnings.push(`group ${groupId} has no cabinet body — no run`);
    return nothing;
  }
  // A pedestal or wall-hung basin is its own body: there is no cabinet for a
  // run to re-solve, and a run whose span is narrower than any carcass would
  // emit one anyway (plan open decision #3). Refused, not degraded.
  if (isStandaloneSink(cabinet)) {
    warnings.push(
      `group ${groupId} is a standalone basin, not cabinetry — no run`
    );
    return nothing;
  }
  // Already converted (or already a run's): idempotent, so a reprocess and a
  // second click both do nothing rather than mint a duplicate.
  if (cabinet.runId) return nothing;

  const onWall = vanityOnWall(room, cabinet, groupObjects, groupId);
  if (onWall.warning) {
    warnings.push(onWall.warning);
    return nothing;
  }
  const { wall, wallId, extent, lo, hi, basins } = onWall;

  // WHICH WAY THIS VANITY IS SIZED, decided here and stamped on the run —
  // `sizing.js` says why it cannot be inferred from the depth later.
  const sizing = options.source === 'scan' ? AS_BUILT : STANDARD;

  // One appliance bay per basin, anchored where the basin actually stands.
  //
  // A sink reserves its BASE CABINET, not the bowl the scan measured — the
  // rule `deriveScanRuns` states in the same words, and the reason the vanity
  // path needs it just as badly: a ~20" basin reserved AS a 20" bay leaves the
  // 8" either side of it too narrow for any module the family sells, so both
  // come out as filler. Measured over the reference corpus, reserving the bowl
  // put filler in 34 of 34 vanity runs at a median 28% of the run.
  //
  // Depth is omitted for the same reason it is there: the bowl is shallower
  // than the base that carries it, so the bay falls back to the run's own
  // cabinet depth.
  //
  // CAPPED at what the vanity can hold, which the kitchen never has to think
  // about: a 24" base is the narrowest standard, and a bathroom vanity is
  // routinely narrower than that (22.4" and 23.6" in the corpus), while a
  // basin centred 11.9" from the end of a 58.6" double leaves 11.9", not 12".
  // Uncapped, the bay hung 0.2"-0.8" past the end on 3 of 34 corpus runs and
  // the packer reported six unsatisfied pieces — a complaint about a rounding
  // error, on vanities that are fine. The cap is the widest base that stays
  // inside the run while staying centred on its bowl, floored at the bowl
  // itself so a reservation never claims less than the basin standing in it.
  // The ONE sink-bay rule, asked for the WHOLE run with this run's sizing
  // intent (`sizing.js`), which follows the SOURCE: a SCANNED vanity is the
  // dimensions the room contains, so it is as-built and its sink bases are the
  // boxes that are actually there; a vanity the BUILDER built was built out of
  // standard sizes, so it takes standard bases with real cabinetry beside
  // them. The depth picker re-asks the same rule when the user changes the
  // answer. Asked for the whole run because a bay is bounded by its NEIGHBOUR
  // as much as by the run's end.
  //
  // IN THE LINE the vanity is built in, when it is built in one
  // (`options.family` with a size catalog, 2026-10-21): the line's own sink
  // units, the double-bowl unit for two bowls that fit in it — the fill builds
  // the rest of the planned width. Nothing in the line fits: the bays are the
  // standard rule's, the run stays in the line, and the warning says why.
  const { bays: placed, refusal } = vanitySinkPlan({
    basins,
    lo,
    hi,
    sizing,
    run: options.family
      ? { family: options.family, profile: VANITY_PROFILE_ID }
      : null,
  });
  if (refusal) {
    warnings.push(
      `vanity ${cabinet.id}: ${refusal} — it is built in the line all the ` +
        'same, at a size the line does not make, and prices as missing'
    );
  }
  const bays = placed.map((bay) => ({
    ...applianceBayFromLegacy({
      id: bay.id,
      type: 'sink',
      segmentIndex: 0,
      centerParam: bay.centerParam,
      fixtureGroupId: groupId,
      footprint: { width: bay.width },
    }),
    // Where the BOWL belongs, when that is not the box's middle — see
    // `vanitySinkBay.js`. Without it the first re-solve of a scanned double
    // vanity seated both bowls to the middles of their boxes.
    ...(Number.isFinite(bay.applianceParam)
      ? { applianceParam: bay.applianceParam }
      : {}),
    // A line's double-bowl unit: ONE bay holding both bowls, which the fill
    // stamps on the box it builds (`appliances.js`) so the estimate prices
    // the double rather than the single of the same width.
    ...(bay.sinkCount > 1 ? { sinkCount: bay.sinkCount } : {}),
  }));
  if (basins.length && !bays.length) {
    warnings.push(
      `vanity ${cabinet.id}: no basin could be placed on wall ${wallId}`
    );
  }

  const countertopForOverhang = getVanityCountertop(groupObjects);
  // THE OVERHANG THAT EXISTS, not one re-derived from a rule. The scan path
  // anchors on the ENVELOPE — "what RoomPlan measured is the vanity's outer
  // envelope, and that is the COUNTER" — and sets the carcass back from it by
  // whatever front strip the room GRANTS, which is often nothing: measured
  // over dev's 317 vanities, 189 carry the full 1.25", **82 carry none at
  // all** (a toilet standing in the strip) and 46 carry more. A re-solve that
  // re-derived the overhang would push those 82 counters 1.25" into space the
  // room already refused, and it silently shaved 0.27" off every other one,
  // because the run profile's 1" and the scan path's 1.25" are different
  // numbers for one thing. So the run carries the measurement, and the fit
  // rule is left to the runs that HAVE no measurement — the drawn ones.
  const measuredOverhang =
    countertopForOverhang?.dimensions?.z != null
      ? countertopForOverhang.dimensions.z - cabinet.dimensions.z
      : null;
  if (measuredOverhang != null && measuredOverhang < -1e-9) {
    warnings.push(
      `vanity ${cabinet.id}: its counter is ` +
        `${(-measuredOverhang / IN_M).toFixed(2)}" SHALLOWER than its carcass ` +
        '— recording no overhang rather than a negative one'
    );
  }

  const run = {
    id: vanityRunId(cabinet.id),
    source: options.source === 'scan' ? 'scan' : 'auto',
    profile: VANITY_PROFILE_ID,
    // Standard sizes, or match what is there (`sizing.js`). Stamped rather
    // than inferred: a built vanity's depth IS the standard one, so the
    // inference called every generated vanity as-built and gave it a
    // full-width sink base.
    sizing,
    // The depth the vanity WAS BUILT AT, carried as the run's stored choice.
    // Not snapped to the profile's offered chips (`runDepthSpec` says why): a
    // vanity measured at 18.2" is 18.2" deep, and rounding it to make a
    // picker tidy would rebuild a room's cabinetry to a size nobody has.
    //
    // Read straight off the cabinet, NOT through `measuredCabinetDepth`. That
    // helper takes the median depth of the bodies at KITCHEN base height
    // (28.5"-40.5"), because a kitchen run is a mixed pile it has to pick
    // base cabinets out of. Here there is exactly one body and the group has
    // already said it is the vanity — and a scanned vanity is routinely
    // outside that band (F6B82D01's is 17.9" tall), so the gate discarded the
    // measurement and left the run at the profile's 21". Harmless until the
    // first bay edit, which is precisely when it would have silently rebuilt
    // an 18.2" vanity 3" deeper.
    depth:
      Number.isFinite(cabinet.dimensions.z) && cabinet.dimensions.z > 0
        ? cabinet.dimensions.z
        : undefined,
    segments: [{ wallId, kind: 'real', ...extent }],
    bays,
  };
  if (run.depth == null) delete run.depth;
  // Built under a vendor cabinet line (design 2026-10-05, D6): the run is in
  // that family, wearing a look it sells — and the company offers (issue
  // #2460) — so a re-fill keeps its sizes. With no line, it wears what the
  // vanity wears (`wornLookFields`).
  const familyFields = familyFieldsForNewRun(
    room,
    options.family,
    options.companyOffering
  );
  Object.assign(
    run,
    familyFields.family
      ? familyFields
      : wornLookFields(room, cabinet, getVanityCountertop(groupObjects))
  );
  if (measuredOverhang != null) {
    run.counterOverhang = Math.max(0, measuredOverhang);
  }
  // WHAT THE SCAN SAID, kept apart from what the run is currently BUILT at.
  //
  // `depth` and `counterOverhang` above are the run's live values, and a
  // user's depth pick overwrites them — after which nothing could tell the
  // measurement from the choice, so "put it back the way it was scanned"
  // had no answer and a stale overhang rode along into a standard vanity
  // (a 21" carcass under a 21" counter, flush with its own doors, on the 82
  // dev vanities whose room granted no front strip).
  //
  // Recorded once, here, and never rewritten by an edit. The picker offers it
  // back as "As built" and `applyRunDepth` restores the envelope with it.
  const measured = {};
  // THE HEIGHT THE ROOM CONTAINS, recorded for the same reason as the depth
  // and read back by `runBodyHeight`: as-built used to honour the depth and
  // standardise the height, which lifted a converted 30 ¼" vanity's counter to
  // the profile's 32" and left the scanned basin's rim 1 ¾" below the slab.
  // Straight off the cabinet, like the depth — see the note there on why
  // `measuredCabinetDepth` is the wrong instrument for a vanity.
  if (Number.isFinite(cabinet.dimensions?.y) && cabinet.dimensions.y > 0) {
    measured.height = cabinet.dimensions.y;
  }
  // WHERE THE SCAN PUT THE BOWLS, so "As built" has something to restore.
  // A standard pick re-seats them to their cabinets' middles (product
  // directive 2026-09-27) and the run's own promise is that switching away
  // and back is not a one-way door — which it would be for the basins, since
  // nothing else remembers where they were.
  // Keyed by the BASIN's own id, not the bay's: a bay id is derived
  // (`bay_<applianceId>`) and a re-derive could mint a different one, while the
  // basin is the object whose place is being remembered. The reader looks it up
  // by `bay.applianceId` for the same reason.
  const measuredBasins = placed
    .map((bay) => ({ id: bay.id, centerParam: bay.applianceParam }))
    .filter((b) => Number.isFinite(b.centerParam));
  if (measuredBasins.length) measured.basins = measuredBasins;
  if (run.depth != null) measured.depth = run.depth;
  if (run.counterOverhang != null) {
    measured.counterOverhang = run.counterOverhang;
  }
  if (Object.keys(measured).length) run.measured = measured;
  // A carcass too shallow to carry a catalog bowl even after spending the
  // whole overhang budget is REPORTED, not refused: the run is still the right
  // record of the cabinetry that is there, and a 7.0"-deep vanity (dev's
  // shallowest, needing 8.3" of overhang) is far likelier to be RoomPlan
  // reading a door face than a real console. The fill will build a procedural
  // basin narrower than anything the catalog sells; saying so is what lets
  // someone decide whether that box wanted the wall-hung unit instead.
  if (run.depth != null && !carcassCanCarryBasin(run.depth)) {
    warnings.push(
      `vanity ${cabinet.id}: a ${(run.depth / IN_M).toFixed(1)}" carcass ` +
        'cannot carry a catalog basin even at the full counter overhang — ' +
        'the run is derived and the fill draws the built-in basin, shrunk to ' +
        'the box, rather than a product that does not fit'
    );
  }

  // The bodies the run now owns. The countertop is the cabinet's own, found
  // through the group rather than by geometry — a vanity's slab is built to
  // its cabinet, and the group is the record of that.
  const countertop = getVanityCountertop(groupObjects);
  const ownedIds = new Set([cabinet.id, countertop?.id].filter(Boolean));
  const tagged = roomObjects.map((o) =>
    o?.id && ownedIds.has(o.id) && !o.runId ? { ...o, runId: run.id } : o
  );

  const framed = stampRunFrames(run, (seg) =>
    seg.wallId === wallId ? wall : null
  );
  return {
    run: framed,
    objects: stampApplianceBays(tagged, [framed]),
    warnings,
  };
}

/**
 * Derive runs for EVERY convertible vanity group in a room.
 *
 * The reprocess entry point (phase 3b's automatic half): idempotent, and a
 * group it cannot convert is reported rather than skipped silently.
 *
 * @param {Object} room
 * @param {Object} [options]  `source`, forwarded.
 * @returns {{runs: Object[], objects: Object[], warnings: string[]}}
 */
function deriveVanityRuns(room, options = {}) {
  const groupIds = [
    ...new Set(
      (room?.objects || [])
        .filter((o) => o?.fixtureGroupId)
        .map((o) => o.fixtureGroupId)
    ),
  ];
  let objects = room?.objects || [];
  const runs = [];
  const warnings = [];
  for (const fixtureGroupId of groupIds) {
    const out = vanityToRun(
      { ...room, objects },
      { ...options, fixtureGroupId }
    );
    // Only a group that HAS a cabinet is a candidate; the rooms this runs over
    // are full of toilets, tubs and showers, and reporting "no cabinet body"
    // for each of them would bury the warnings that matter.
    if (!out.run) {
      const groupObjects = objects.filter(
        (o) => o?.fixtureGroupId === fixtureGroupId
      );
      const cabinet = getVanityCabinet(groupObjects);
      if (cabinet && !cabinet.runId && !isStandaloneSink(cabinet)) {
        warnings.push(...out.warnings);
      }
      continue;
    }
    objects = out.objects;
    runs.push(out.run);
    warnings.push(...out.warnings);
  }
  return { runs, objects, warnings };
}

/**
 * Whether to OFFER "convert this vanity to a cabinet run" on `object`.
 *
 * The gate is the DERIVE ITSELF — a dry run, thrown away — so the affordance
 * and the action cannot disagree. That matters here specifically: a convert
 * affordance existed once and was removed because it gated on object TYPE
 * alone (`lower-cabinet`) and so offered itself on the vanities it could not
 * convert (`scanRuns.js`'s history note). The cheap checks in front of it are
 * only there to keep a panel render from measuring wall geometry for every
 * box in the room.
 *
 * Room KIND is deliberately not part of it. What makes this a vanity is its
 * fixture GROUP, not the room it stands in — bathroom post-processing builds
 * the same assembly for a sink-on-a-cabinet in a bedroom, and that vanity
 * deserves the same editing surface.
 *
 * @param {Object} room
 * @param {Object} object  the selected object.
 * @returns {boolean}
 */
function canConvertVanityToRun(room, object) {
  if (!object?.fixtureGroupId || object.runId) return false;
  if (!isLowerCabinetLike(object)) return false;
  if (!isVanityAssemblyGroupId(object.fixtureGroupId)) return false;
  return !!vanityToRun(room, { fixtureGroupId: object.fixtureGroupId }).run;
}

module.exports = {
  vanityToRun,
  deriveVanityRuns,
  canConvertVanityToRun,
  vanityRunId,
  isVanityRunId,
  vanityOnWall,
};
